/**
 * PendingActionsCard - Shows pending actions requiring user attention
 */

import { Link } from 'react-router-dom';
import type { PendingAction } from '../../api/studios';

interface PendingActionsCardProps {
  actions: PendingAction[];
  isLoading?: boolean;
  onActionClick?: (action: PendingAction) => void;
}

export function PendingActionsCard({ actions, isLoading, onActionClick }: PendingActionsCardProps) {
  const getActionIcon = (type: PendingAction['type']) => {
    switch (type) {
      case 'payment_due':
        return (
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
            />
          </svg>
        );
      case 'confirmation_needed':
        return (
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        );
      case 'survey_pending':
        return (
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
            />
          </svg>
        );
      case 'document_required':
        return (
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
        );
    }
  };

  const getActionColors = (type: PendingAction['type']) => {
    switch (type) {
      case 'payment_due':
        return 'bg-danger-100 text-danger-600 border-danger-200';
      case 'confirmation_needed':
        return 'bg-warning-100 text-warning-600 border-warning-200';
      case 'survey_pending':
        return 'bg-primary-100 text-primary-600 border-primary-200';
      case 'document_required':
        return 'bg-warm-100 text-warm-600 border-warm-200';
    }
  };

  if (isLoading) {
    return (
      <div className="card">
        <h3 className="text-lg font-semibold text-warm-900 mb-6">Action Required</h3>
        <div className="space-y-3">
          {[1, 2].map((i) => (
            <div key={i} className="animate-pulse">
              <div className="h-16 bg-warm-100 rounded-lg" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (actions.length === 0) {
    return (
      <div className="card">
        <h3 className="text-lg font-semibold text-warm-900 mb-6">Action Required</h3>
        <div className="text-center py-6">
          <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-success-100 flex items-center justify-center">
            <svg
              className="w-6 h-6 text-success-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <p className="text-warm-600 text-sm">All caught up!</p>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-warm-900">Action Required</h3>
        <span className="inline-flex items-center justify-center w-6 h-6 text-xs font-bold text-white bg-danger-500 rounded-full">
          {actions.length}
        </span>
      </div>

      <div className="space-y-3">
        {actions.map((action, index) => {
          const isExternal = action.link.startsWith('http');
          const className = `block p-4 rounded-lg border ${getActionColors(action.type)} hover:opacity-90 transition-opacity`;
          const content = (
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 mt-0.5">{getActionIcon(action.type)}</div>
              <div className="flex-1 min-w-0">
                {action.title ? (
                  <>
                    <p className="font-medium text-warm-900">{action.title}</p>
                    <p className="mt-0.5 text-sm text-warm-600">{action.message}</p>
                  </>
                ) : (
                  <p className={`font-medium text-warm-900 ${action.type === 'document_required' ? 'text-sm' : ''}`}>{action.message}</p>
                )}
                {action.due_date && (
                  <p className="mt-1 text-sm text-warm-500">
                    Due: {new Date(action.due_date).toLocaleDateString()}
                  </p>
                )}
              </div>
              <svg
                className="w-5 h-5 text-warm-400 flex-shrink-0"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </div>
          );

          if (isExternal && onActionClick) {
            return (
              <button key={index} onClick={() => onActionClick(action)} className={`${className} w-full text-left cursor-pointer`}>
                {content}
              </button>
            );
          }

          return isExternal ? (
            <a key={index} href={action.link} target="_blank" rel="noopener noreferrer" className={className}>
              {content}
            </a>
          ) : (
            <Link key={index} to={action.link} className={className}>
              {content}
            </Link>
          );
        })}
      </div>
    </div>
  );
}

export default PendingActionsCard;
